import Heading from "componentsTemplate/Heading/Heading";
import NcPlayIcon from "componentsTemplate/NcPlayIcon/NcPlayIcon";
import NcPlayIcon2 from "componentsTemplate/NcPlayIcon2/NcPlayIcon2";
import React, { FC, Fragment, useEffect, useState, useRef } from "react";
import ReactPlayer from "react-player";
import isSafariBrowser from "utils/isSafariBrowser";
import Image from "componentsTemplate/Image";

export interface VideoType {
  id: string;
  title: string;
  thumbnail: string;
}

export interface SectionVideosProps {
  videos?: VideoType[];
  className?: string;
}

const SectionFotos = ({
  attachments,
  className = "",
  url = "/storage/images/posts/events/attachments/",
}: any) => {
  const [isPlay, setIsPlay] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(0);
  const [isRendered, setIsRendered] = useState(false);
  const [imageDimensions, setImageDimensions] = useState<{[key: number]: {width: number, height: number}}>({});
  const imageRefs = useRef<{[key: number]: HTMLImageElement | null}>({});
  const mainViewerRef = useRef<HTMLDivElement | null>(null);
  const headingRef = useRef<HTMLDivElement | null>(null);
  const [showAllImages, setShowAllImages] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detectar se é mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    setIsRendered(true);
  }, []);

  // Função para detectar se a imagem é retrato (portrait)
  const isPortrait = (index: number) => {
    const dimensions = imageDimensions[index];
    if (!dimensions) return false;
    return dimensions.height > dimensions.width;
  };

  // Função para carregar dimensões da imagem
  const handleImageLoad = (index: number) => {
    return () => {
      const imgElement = imageRefs.current[index];
      if (imgElement && imgElement.naturalWidth > 0) {
        setImageDimensions(prev => ({
          ...prev,
          [index]: {
            width: imgElement.naturalWidth,
            height: imgElement.naturalHeight
          }
        }));
      }
    };
  };

  // Função para definir ref da imagem
  const setImageRef = (index: number) => (el: HTMLImageElement | null) => {
    imageRefs.current[index] = el;
  };

  // Função para fazer scroll para o texto "Anexos" no mobile
  const scrollToViewer = () => {
    if (isMobile && headingRef.current) {
      headingRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const renderMainVideo = () => {
    const attachment = attachments[currentVideo];
    const isCurrentPortrait = isPortrait(currentVideo);

    return (
      <div
        ref={mainViewerRef}
        className="group aspect-w-16 aspect-h-16 sm:aspect-h-9 bg-neutral-800 rounded-3xl overflow-hidden 
          border-4 border-white dark:border-neutral-900 sm:rounded-[50px] sm:border-[10px] z-0 relative"
      >
        <Fragment>
          <Fragment>
            {/* Imagem de fundo borrada para retratos */}
            {isCurrentPortrait && (
              <Image
                className="object-cover absolute inset-0 w-full h-full filter blur-sm opacity-50"
                src={
                  process.env.REACT_APP_BACKEND_API_URL + url + attachment?.path
                }
                alt={attachment?.original}
                fill
                sizes="(max-width: 600px) 480px, 800px"
              />
            )}
            
            {/* Imagem principal */}
            <img
              className={`transition-transform group-hover:scale-105 duration-300 absolute inset-0 w-full h-full ${
                isCurrentPortrait 
                  ? "object-contain" 
                  : "object-cover"
              }`}
              src={
                process.env.REACT_APP_BACKEND_API_URL + url + attachment?.path
              }
              alt={attachment?.original}
              onLoad={handleImageLoad(currentVideo)}
              ref={setImageRef(currentVideo)}
            />
            {/* Overlay de descrição no hover */}
            {attachment?.description && (
              <div
                className={`absolute inset-0 flex items-end z-10 pointer-events-none transition-opacity duration-200 ${
                  isMobile
                    ? "opacity-100"
                    : "opacity-0 group-hover:opacity-100"
                }`}
              >
                <div className="w-full rounded-t-lg bg-neutral-900/60 backdrop-blur-md px-4 py-3">
                  <p className="text-xs sm:text-sm text-white  overflow-hidden text-ellipsis">
                    {attachment.description}
                  </p>
                </div>
              </div>
            )}
          </Fragment>
        </Fragment>
      </div>
    );
  };

  const renderSubVideo = () => {
    // No mobile, mostrar apenas 8 fotos inicialmente (2 linhas de 4)
    const maxMobileImages = 8;
    const imagesToShow = isMobile && !showAllImages 
      ? attachments.slice(0, maxMobileImages)
      : attachments;

    const images = imagesToShow.map((attachment: any, index: any) => {
      return (
        <div
          className="group aspect-h-16 aspect-w-16 rounded-2xl cursor-pointer overflow-hidden sm:aspect-h-12 sm:rounded-3xl lg:aspect-h-9 z-0"
          style={{ height: "65px" }}
          title={attachment?.original}
          onClick={() => {
            setCurrentVideo(index);
            scrollToViewer();
          }}
          key={String(index)}
        >
          <img
            className="object-cover transition-transform group-hover:scale-110 duration-300 absolute inset-0 w-full h-full"
            src={process.env.REACT_APP_BACKEND_API_URL + url + attachment?.path}
            title={attachment?.original}
            alt={attachment?.original}
            onLoad={handleImageLoad(index)}
            ref={setImageRef(index)}
          />
        </div>
      );
    });

    // Adicionar botão "Ver mais" no mobile se houver mais fotos
    if (isMobile && attachments.length > maxMobileImages && !showAllImages) {
      images.push(
        <div
          key="show-more"
          className="mt-5 w-full group aspect-h-16 aspect-w-16 rounded-2xl cursor-pointer overflow-hidden sm:aspect-h-12 sm:rounded-3xl lg:aspect-h-9 z-0 bg-gray-100 dark:bg-gray-800 flex items-center justify-center"
          style={{ height: "65px" }}
          onClick={() => setShowAllImages(true)}
        >
          <div className="text-center">
            <div className="text-xs font-medium text-gray-600 dark:text-gray-300">
              +{attachments.length - maxMobileImages}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Ver mais
            </div>
          </div>
        </div>
      );
    }

    return images;
  };

  return (
    <div className={`nc-SectionFotos ${className}`} ref={headingRef}>
      <Heading desc="">Anexos</Heading>

      <div className="flex flex-col relative lg:flex-row">
        {/* <div className="absolute -top-4 -bottom-4 -right-4 w-2/3 rounded-3xl bg-primary-100/40 z-0 sm:rounded-[50px] md:top-0 md:bottom-0 md:right-0 xl:w-1/2 dark:bg-neutral-800/40"></div> */}
        <div className="flex-grow relative pb-2 sm:pb-4 lg:pb-0 lg:pr-5 xl:pr-6">
          {isRendered && renderMainVideo()}
        </div>
        <div className="flex-shrink-0">
          <div className="grid gap-2 grid-cols-4 sm:gap-6 lg:grid-cols-1 lg:w-36 xl:w-40 grid-container">
            {isRendered && renderSubVideo()}
          </div>
          
          {/* Botão "Ver menos" no mobile quando todas as fotos estão sendo exibidas */}
          {isMobile && showAllImages && attachments.length > 8 && (
            <button
              onClick={() => setShowAllImages(false)}
              className="mt-3 w-full px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              Ver menos
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SectionFotos;
